import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import * as notesApi from 'lib/api/notes';
import { useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { QUERY_FETCH_NOTES } from './constants';
import Skeleton from '../Skeleton';
import NotePost from './NotePost';
import EmptyNotes from './EmptyNotes';
import ModalEditNote from './ModalEditNote';

const NOTES_LIMIT = 10;

const NotesPost = () => {
  const queryClient = useQueryClient();

  const [notes, setNotes] = useState<NoteItem[]>([]);

  const [editModal, setEditModal] = useState({
    visible: false,
    symbol: null,
    companyName: null,
  });

  const {
    isLoading,
    isRefetching,
    hasNextPage,
    data,
    error,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_FETCH_NOTES],
    queryFn:
      ({ pageParam = undefined }) => notesApi.getNotes({ cursor: pageParam, limit: NOTES_LIMIT }),
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage?.data?.data;
      if (!pagination || pagination.next_cursor === '') {
        return undefined;
      }
      return pagination.next_cursor;
    },
    onSuccess: (response) => {
       const datasource = response.pages
        .flatMap(({ data: { data: responseData } }) => responseData.notes);

      setNotes(datasource);
    },
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const handleLoadMore = async (isVisible: boolean) => {
    if (isVisible && !isLoading && notes.length > 0 && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleClickEdit = (id: number) => {
    const note = notes.find((item) => item.id === id);
    if (note) {
      setEditModal((prev) => ({
        ...prev,
        visible: true,
        companyName: note.company.name,
        symbol: note.company.symbol,
      }));
    }
  };

  const handleCloseModalEdit = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_FETCH_NOTES] }).then(() => {
      setEditModal({
        visible: false,
        companyName: null,
        symbol: null,
      });
    });
  };

  if (!data || isRefetching) {
    return (
      <>
        {[...new Array(5)].map(() => <Skeleton />)}
      </>
    );
  }

  if (!isRefetching && notes.length === 0) {
    return <EmptyNotes />;
  }

  return (
    <div>
      {notes.map((item) => (
        <NotePost
          data={item}
          key={item.id}
          handleClickEdit={handleClickEdit}
        />
      ))}
      {!error && notes.length > 0 && hasNextPage && (
        <VisibilitySensor
          onChange={handleLoadMore}
          minTopValue={200}
        >
          <Skeleton />
        </VisibilitySensor>
      )}
      <ModalEditNote
        visible={editModal?.visible}
        symbol={editModal?.symbol}
        companyName={editModal?.companyName}
        onClose={handleCloseModalEdit}
      />
    </div>
  );
};

export default NotesPost;
